import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardMedia,
  Chip,
  Grid,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import noImage from '../assets/no-image.png';
import { useNavigate } from 'react-router-dom';
import GoogleIcon from '../../assets/googleIcon.png';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';

import PageState from '../layouts/PageState';
import { useV2Session } from '../state/SessionProvider';
import { useV2Common } from '../state/CommonProvider';
import { getGenericLanding, getCustomerLanding } from '../services/store';
import LocationBanner from '../components/LocationBanner';
import HowWeRaiseSection from '../components/landing/HowWeRaiseSection';
import HonestBiteJourneySection from '../components/landing/HonestBiteJourneySection';
import LandingGreeting from '../components/landing/LandingGreeting';
import LandingPopupBanner from '../components/landing/LandingPopupBanner';
import LandingSearch from '../components/landing/LandingSearch';
import LandingTrustStrip from '../components/landing/LandingTrustStrip';
import MostLovedSection from '../components/landing/MostLovedSection';
import { useV2StoreResolution } from '../hooks/useV2StoreResolution';
import { paths } from '../config/paths';
import { categoryNameToSlug } from '../../routing/categorySlugMap';

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

function BannerCarousel({ mobileBanners = [], desktopBanners = [], variant = 'default' }) {
  const navigate = useNavigate();
  const { isMobile } = useV2Common();
  const isHero = variant === 'hero';
  const banners = isMobile
    ? (mobileBanners.length ? mobileBanners : desktopBanners)
    : (desktopBanners.length ? desktopBanners : mobileBanners);

  if (!banners.length) return null;

  const handleClick = (route) => {
    if (route) navigate(route);
  };

  return (
    <Box
      sx={{
        width: '100%',
        mb: { xs: 3, md: 0 },
        overflow: 'hidden',
        ...(isHero && {
          borderRadius: 2.5,
          aspectRatio: { md: '3.2 / 1', lg: '3.5 / 1' },
          maxWidth: '100%',
          width: '100%',
          boxSizing: 'border-box',
          boxShadow: '0 8px 28px rgba(0,0,0,0.07)',
          '& .swiper': { maxWidth: '100%', boxSizing: 'border-box', height: '100%' },
          '& .swiper-wrapper, & .swiper-slide': { height: '100%' },
          '& .swiper-pagination': { bottom: 10 },
          '& .swiper-pagination-bullet': {
            bgcolor: '#fff',
            opacity: 0.55,
          },
          '& .swiper-pagination-bullet-active': { opacity: 1 },
        }),
      }}
    >
      <Swiper
        modules={[Autoplay, Pagination]}
        autoplay={{ delay: 350000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        loop
        style={{
          borderRadius: 12,
          overflow: 'hidden',
          width: '100%',
          height: isHero ? '100%' : undefined,
        }}
      >
        {banners.map((b, i) => (
          <SwiperSlide key={i}>
            <Box
              component="img"
              src={b.url}
              alt={b.title || 'banner'}
              onClick={() => handleClick(b.route)}
              sx={{
                width: '100%',
                height: isHero ? '100%' : { xs: 160, sm: 220, md: 280, lg: 320 },
                objectPosition: 'center',
                display: 'block',
                borderRadius: isHero ? 0 : { xs: 2, md: 2.5 },
                cursor: b.route ? 'pointer' : 'default',
                boxShadow: isHero ? 'none' : { md: '0 12px 40px rgba(0,0,0,0.08)' },
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}

function DesktopLocationSearchRow({ locationProps, storeId }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { md: 2, lg: 2.5 },
        mb: 1,
        width: '100%',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          flex: { md: '1 1 48%', lg: '1 1 52%' },
          minWidth: 0,
          maxWidth: { md: 480, lg: 320 },
        }}
      >
        <LocationBanner {...locationProps} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <LandingSearch storeId={storeId} compact />
      </Box>
    </Box>
  );
}

function DesktopHeroBanner({ customer, mobileBanners, desktopBanners }) {
  return (
    <Box sx={{ mb: 3, width: '100%', minWidth: 0 }}>
      <LandingGreeting customer={customer} compact />
      <BannerCarousel
        mobileBanners={mobileBanners}
        desktopBanners={desktopBanners}
        variant="hero"
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

function CategoryTile({ category, onSelect }) {
  const name = category?.name ?? 'Category';
  const img = category?.image || noImage;

  return (
    <Card
      elevation={0}
      onClick={() => onSelect(category)}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
        '&:active': { bgcolor: 'action.hover' },
        '&:hover': {
          borderColor: { md: 'primary.light' },
          boxShadow: { md: 2 },
          transform: { md: 'translateY(-2px)' },
        },
      }}
    >
      <CardMedia
        component="img"
        image={img}
        alt={name}
        sx={{
          width: '100%',
          minHeight: { xs: 120, md: 96 },
          maxHeight: { md: 'auto' },
          objectFit: 'cover',
          bgcolor: 'grey.100',
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = noImage;
        }}
      />
      <Box
        sx={{
          px: { xs: 1.25, md: 0.75 },
          py: { xs: 1.25, md: 0.75 },
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          textAlign="center"
          sx={{
            width: '100%',
            lineHeight: 1.25,
            fontSize: { xs: '0.9rem', md: '0.8rem' },
          }}
        >
          {name}
        </Typography>
      </Box>
    </Card>
  );
}

function CategoryGrid({ categories, onSelect }) {
  if (!categories?.length) return null;
  return (
    <Box mb={{ xs: 4, md: 7 }}>
      <Typography
        variant="subtitle1"
        component="h2"
        sx={{
          fontWeight: 700,
          mb: { xs: 1.5, md: 2 },
          fontSize: { xs: '0.95rem', md: '1.25rem' },
          letterSpacing: { md: '-0.02em' },
        }}
      >
        Shop by category
      </Typography>
      <Grid container spacing={{ xs: 1.5, md: 1.5 }} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        {categories.map((cat) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={3}
            lg={2}
            key={cat.documentId || cat.id || cat.name}
            sx={{ minWidth: 0 }}
          >
            <CategoryTile category={cat} onSelect={onSelect} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Latest orders teaser (logged-in only)
// ---------------------------------------------------------------------------

const STATUS_LABEL = {
  pending: 'Order placed',
  confirmed: 'Confirmed',
  processing: 'Being prepared',
  out_for_delivery: 'Out for delivery',
};

const STATUS_COLOR = {
  pending: 'default',
  confirmed: 'primary',
  processing: 'warning',
  out_for_delivery: 'success',
};

const LATEST_ORDERS_PREVIEW = 1;

function formatLatestOrderTotal(order) {
  const n = Number(order?.totalAmount ?? order?.total ?? 0);
  if (!Number.isFinite(n)) return '—';
  return `₹${Math.round(n)}`;
}

function latestOrderProductLine(order) {
  const raw = order?.productLines;
  const lines = Array.isArray(raw)
    ? raw
    : Array.isArray(order?.products)
      ? order.products.map((p) => ({
          name: String(p?.name || p?.productName || '').trim() || 'Item',
          quantity: Math.max(1, Math.floor(Number(p?.quantity) || 1)),
        }))
      : [];
  if (!lines.length) return null;
  return lines
    .map(({ name, quantity }) => (quantity > 1 ? `${name} ×${quantity}` : name))
    .join(', ');
}

function LatestOrders({ orders, compact = false }) {
  const navigate = useNavigate();

  if (!orders?.length) return null;

  const hasMore = orders.length > LATEST_ORDERS_PREVIEW;
  const visibleOrders = orders.slice(0, LATEST_ORDERS_PREVIEW);

  return (
    <Box sx={{ mb: compact ? 0 : 3, mt: compact ? 0.5 : 0 }}>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        mb={compact ? 0.75 : 1}
        sx={{ fontSize: compact ? '0.95rem' : { md: '1.05rem' } }}
      >
        Latest order
      </Typography>
      <Stack spacing={1}>
        {visibleOrders.map((o) => {
          const fullOrderId = String(o.orderId || o.id || '').trim();
          const shortId =
            fullOrderId.length > 6 ? fullOrderId.slice(-6).toUpperCase() : fullOrderId.toUpperCase();
          const productLine = latestOrderProductLine(o);
          return (
            <Box
              key={fullOrderId || o.orderId || o.id}
              onClick={() => {
                if (fullOrderId) {
                  navigate(paths.accountOrder(fullOrderId));
                } else {
                  navigate(paths.account);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                  boxShadow: { md: 2 },
                  borderColor: { md: 'primary.light' },
                },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500}>
                  Order #{shortId}
                </Typography>
                {productLine ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, lineHeight: 1.35 }}
                  >
                    {productLine}
                  </Typography>
                ) : null}
                <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75 }}>
                  Total {formatLatestOrderTotal(o)}
                </Typography>
              </Box>
              <Chip
                label={STATUS_LABEL[o.order_status] || o.order_status}
                color={STATUS_COLOR[o.order_status] || 'default'}
                size="small"
                sx={{ flexShrink: 0 }}
              />
            </Box>
          );
        })}
      </Stack>
      {hasMore ? (
        compact ? (
          <Box sx={{ mt: 1 }}>
            <Box
              component="button"
              type="button"
              onClick={() => navigate(paths.account)}
              sx={{
                border: 'none',
                margin: 0,
                padding: 0,
                cursor: 'pointer',
                typography: 'body2',
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: 'inherit',
                bgcolor: 'transparent',
                '&:hover': { color: 'primary.dark' },
              }}
            >
              View all orders →
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
            <Box
              component="button"
              type="button"
              onClick={() => navigate(paths.account)}
              sx={{
                border: 'none',
                margin: 0,
                cursor: 'pointer',
                borderRadius: 2,
                px: 2.5,
                py: 1,
                typography: 'body2',
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: 'inherit',
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              View all orders
            </Box>
          </Box>
        )
      ) : null}
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Naatu fans testimonials
// ---------------------------------------------------------------------------

const TESTIMONIALS = [
  { title: 'Fresh and Delicious!', description: 'The country chicken was incredibly fresh, and the taste was unmatched.', customerName: 'Rahul', location: 'Gachibowli', rating: 5 },
  { title: 'Tender Mutton', description: 'The mutton was tender and cooked perfectly. Highly recommend.', customerName: 'Sneha', location: 'Kondapur', rating: 4.5 },
  { title: 'Farm Fresh Eggs!', description: 'The eggs were fresh and organic, just like they promised.', customerName: 'Arjun', location: 'Madhapur', rating: 5 },
  { title: 'Amazing Quality!', description: 'The country chicken was so tender and juicy. Perfect for a family meal.', customerName: 'Meena', location: 'Banjara Hills', rating: 4.8 },
  { title: 'Excellent Service!', description: 'The mutton was delivered on time and was super fresh. Great experience!', customerName: 'Vikram', location: 'Jubilee Hills', rating: 5 },
];

function TestimonialCard({ t }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 160, md: 176 },
        overflow: 'hidden',
        bgcolor: '#A4243D',
        color: '#fff',
      }}
    >
      <Box sx={{ flex: 1, overflow: 'hidden', p: 1.25 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom noWrap>
          {t.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {t.description}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: '#FFF0D9',
          px: 1,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: '#A4243D', color: '#fff' }}>
            {t.customerName[0]}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#A4243D', lineHeight: 1.2 }}>
              {t.customerName}
            </Typography>
            <Typography sx={{ fontSize: 8, color: '#A4243D', lineHeight: 1.2 }}>
              {t.location}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="img" src={GoogleIcon} alt="Google" sx={{ width: 14, height: 14 }} />
            <Typography sx={{ fontSize: 8, color: '#000' }}>Google Rating</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Typography sx={{ fontSize: 8, fontWeight: 700, color: '#000' }}>{t.rating}</Typography>
            <Rating value={t.rating} precision={0.5} readOnly sx={{ fontSize: 10 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function NatuFansSays() {
  const { isDesktop } = useV2Common();

  if (isDesktop) {
    return (
      <Box mb={4}>
        <Typography variant="h6" fontWeight={700} mb={2} sx={{ letterSpacing: '-0.02em' }}>
          What our Naatu fans say
        </Typography>
        <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
          {TESTIMONIALS.map((t, i) => (
            <Grid item xs={12} sm={6} lg={4} key={i} sx={{ minWidth: 0 }}>
              <TestimonialCard t={t} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box mb={3}>
      <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
        What our Naatu fans say
      </Typography>
      <Box sx={{ overflow: 'hidden', mx: -2, px: 2 }}>
        <Swiper spaceBetween={10} slidesPerView="auto">
          {TESTIMONIALS.map((t, i) => (
            <SwiperSlide key={i} style={{ width: 200, maxWidth: 200 }}>
              <TestimonialCard t={t} />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Shared home sections (mock order)
// ---------------------------------------------------------------------------

function LandingHomeSections({
  data,
  loggedIn,
  locationProps,
  storeId,
  onCategorySelect,
}) {
  return (
    <>
      <LocationBanner {...locationProps} />
      <LandingTrustStrip />
      <LandingGreeting customer={loggedIn ? data?.customer : null} />
      <LandingSearch storeId={storeId} />
      <BannerCarousel
        mobileBanners={data?.mobileBanners}
        desktopBanners={data?.desktopBanners}
      />
      <MostLovedSection products={data?.mostLoved ?? []} />
      {loggedIn ? <LatestOrders orders={data?.currentOrders} /> : null}
      <CategoryGrid categories={data?.categories} onSelect={onCategorySelect} />
      <HowWeRaiseSection />
      <HonestBiteJourneySection />
      <NatuFansSays />
    </>
  );
}

// ---------------------------------------------------------------------------
// Landing page
// ---------------------------------------------------------------------------

export default function Landing() {
  const { loggedIn, mobile } = useV2Session();
  const { showError, isDesktop } = useV2Common();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const addresses = useMemo(() => data?.addresses ?? [], [data?.addresses]);
  const {
    resolving: locationResolving,
    error: locationError,
    permission: locationPermission,
    cache: locationCache,
    syncFromCache: syncLocationCache,
    reresolveFromGps: detectLocation,
  } = useV2StoreResolution({
    addresses,
    enabled: !loading,
    loggedIn,
  });

  const storeId = locationCache?.storeId ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = loggedIn && mobile
        ? await getCustomerLanding({ mobile })
        : await getGenericLanding();
      setData(result.data);
    } catch (err) {
      setError(err);
      if (err.recoverable === false) showError({ message: err.message });
    } finally {
      setLoading(false);
    }
  }, [loggedIn, mobile, showError]);

  useEffect(() => { load(); }, [load]);

  const locationProps = {
    cache: locationCache,
    resolving: locationResolving,
    error: locationError,
    permission: locationPermission,
    onDetect: detectLocation,
    onLocationPicked: syncLocationCache,
    addresses,
  };

  const onCategorySelect = (cat) => {
    navigate(paths.category(categoryNameToSlug(cat.name)));
  };

  return (
    <PageState loading={loading} error={error} onRetry={load} loadingMessage="Loading…">
      <Box
        sx={{
          px: { xs: 2.5, sm: 3, md: 3, lg: 4 },
          pt: { xs: 1, md: 2 },
          pb: { xs: 10, md: 2 },
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {isDesktop ? (
          <>
            <DesktopLocationSearchRow locationProps={locationProps} storeId={storeId} />
            <LandingTrustStrip />
            <Box sx={{ mt: { md: 2 } }} />
            <DesktopHeroBanner
              customer={loggedIn ? data?.customer : null}
              mobileBanners={data?.mobileBanners}
              desktopBanners={data?.desktopBanners}
            />
            <MostLovedSection products={data?.mostLoved ?? []} />
            <CategoryGrid categories={data?.categories} onSelect={onCategorySelect} />
            <HowWeRaiseSection />
            <HonestBiteJourneySection />
            <NatuFansSays />
          </>
        ) : (
          <LandingHomeSections
            data={data}
            loggedIn={loggedIn}
            locationProps={locationProps}
            storeId={storeId}
            onCategorySelect={onCategorySelect}
          />
        )}
        <LandingPopupBanner popupBanner={data?.popupBanner ?? null} />
      </Box>
    </PageState>
  );
}
